import { Box3, Color, Mesh, MeshBasicMaterial, PlaneGeometry, Scene, Vector3 } from 'three';
import { PALETTE, POPULATION } from '../../config/settings';
import { Updatable } from '../../core/types';
import { pick, randFloat, randInt } from '../../utils/random';
import { BuildingRegistry } from '../../world/BuildingRegistry';

interface Billboard {
  readonly mesh: Mesh;
  readonly color: Color;
  readonly phase: number;
  readonly rate: number;
}

/** Glowing, pulsing signs mounted on the tall downtown buildings. */
export class NeonBillboards implements Updatable {
  private readonly billboards: Billboard[] = [];
  private elapsed = 0;

  constructor(scene: Scene, registry: BuildingRegistry) {
    const tall = registry.boxes.filter((b) => b.max.y - b.min.y > 85);
    const count = Math.min(POPULATION.BILLBOARDS, tall.length);
    for (let i = 0; i < count; i++) {
      const billboard = this.createBillboard(tall[randInt(tall.length)]);
      scene.add(billboard.mesh);
      this.billboards.push(billboard);
    }
  }

  update(dt: number): void {
    this.elapsed += dt;
    for (const b of this.billboards) {
      const pulse = 0.45 + 0.55 * Math.sin(this.elapsed * b.rate + b.phase);
      (b.mesh.material as MeshBasicMaterial).color.copy(b.color).multiplyScalar(0.4 + 0.6 * pulse);
    }
  }

  private createBillboard(box: Box3): Billboard {
    const size = box.getSize(new Vector3());
    const centre = box.getCenter(new Vector3());
    const colorHex = pick(PALETTE.neon);
    const mesh = new Mesh(
      new PlaneGeometry(size.x * 0.72, Math.min(20, size.y * 0.22)),
      new MeshBasicMaterial({ color: colorHex, toneMapped: false }),
    );

    const y = box.min.y + randFloat(size.y * 0.4, size.y * 0.7);
    const face = randInt(4);
    if (face === 0) {
      mesh.position.set(centre.x, y, box.max.z + 0.3);
    } else if (face === 1) {
      mesh.position.set(centre.x, y, box.min.z - 0.3);
      mesh.rotation.y = Math.PI;
    } else if (face === 2) {
      mesh.position.set(box.max.x + 0.3, y, centre.z);
      mesh.rotation.y = Math.PI / 2;
    } else {
      mesh.position.set(box.min.x - 0.3, y, centre.z);
      mesh.rotation.y = -Math.PI / 2;
    }
    return { mesh, color: new Color(colorHex), phase: randFloat(0, 6), rate: randFloat(2, 5) };
  }
}
